import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'

import { supabase } from '@/lib/supabase/cliente'

/**
 * EL AVISO QUE LLEGA CON LA APP CERRADA (FCM), EL QUE EL PUSH WEB NO PUEDE.
 *
 * El push web (src/modules/avisos/usePush.ts) vive dentro del navegador: en el
 * APK, que es un WebView, no despierta al teléfono con la app cerrada. Este
 * módulo usa el canal nativo de Android —Firebase Cloud Messaging— que entrega
 * la notificación aunque la aplicación esté cerrada y el teléfono bloqueado.
 *
 * El reparto lo hace el servidor (Edge Function enviar-fcm), no aquí. Lo único
 * que hace el teléfono es:
 *   1. pedir permiso de notificaciones (Android 13+ lo exige a mano),
 *   2. dejar que Firebase le dé un token de ESTE aparato,
 *   3. guardar ese token en la base atado a su perfil (fn_registrar_dispositivo_push).
 * Con eso, enviar-fcm sabe a qué aparatos empujar cada aviso.
 *
 * Solo corre dentro de la app nativa de Android. En el navegador este archivo se
 * importa pero nunca hace nada (la guarda de isNativePlatform lo apaga), y el
 * plugin ni se toca, así que la web sigue con su push web de siempre.
 */

let iniciado = false
/** El último token que Firebase entregó, para poder darlo de baja al salir. */
let ultimoToken: string | null = null

/** ¿Estamos en la app nativa de Android? Es el único sitio donde FCM aplica. */
function esAndroidNativo(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

/**
 * Arranca el push nativo. Idempotente: registra los oyentes una sola vez y pide
 * el permiso. Si el usuario ya lo había concedido, Android lo recuerda y no
 * vuelve a preguntar. Solo hace algo en la app nativa de Android.
 */
export async function iniciarPushNativo(): Promise<void> {
  if (!esAndroidNativo() || iniciado) return
  iniciado = true

  // Cuando Firebase entrega (o renueva) el token de este aparato, se guarda.
  await PushNotifications.addListener('registration', (token) => {
    ultimoToken = token.value
    void supabase.rpc('fn_registrar_dispositivo_push', {
      p_token: token.value,
      p_plataforma: 'android',
    })
  })

  // Si el registro falla, no se rompe la app: el técnico simplemente no recibe
  // el push nativo (le queda la campana in-app). No hay nada que reintentar aquí.
  await PushNotifications.addListener('registrationError', () => {
    /* sin token: enviar-fcm sencillamente no tiene a dónde empujar */
  })

  // Android 13+ exige el permiso a mano; en versiones previas viene concedido.
  const permiso = await PushNotifications.checkPermissions()
  let concedido = permiso.receive === 'granted'
  if (permiso.receive === 'prompt' || permiso.receive === 'prompt-with-rationale') {
    const pedido = await PushNotifications.requestPermissions()
    concedido = pedido.receive === 'granted'
  }
  if (!concedido) return

  // Esto dispara el evento 'registration' de arriba con el token de Firebase.
  await PushNotifications.register()
}

/**
 * Da de baja el token de este aparato al cerrar sesión: sin sesión no debe
 * seguir recibiendo avisos de la empresa en este teléfono. Solo aplica en la
 * app nativa de Android.
 */
export async function detenerPushNativo(): Promise<void> {
  if (!esAndroidNativo()) return
  if (ultimoToken) {
    await supabase.rpc('fn_baja_dispositivo_push', { p_token: ultimoToken })
    ultimoToken = null
  }
  await PushNotifications.removeAllListeners()
  iniciado = false
}
